import {
  LOOM_AIRFOIL_CATALOG,
  LOOM_PRIMITIVE_CATALOG,
  LOOM_PROFILE_CATALOG,
  LOOM_TRANSFORM_CATALOG,
  parseLoomAuthoringDocument,
  reduceLoomAuthoringCommand,
  type LoomAirfoil,
  type LoomAuthoringCommand,
  type LoomAuthoringDocument,
  type LoomParameterMetadata,
  type LoomPrimitiveDefinition,
  type LoomProfile2d,
  type LoomSceneNode,
  type LoomTransform,
} from "./loomAuthoringModel";
import { invalidateEditedLoomSectionTreatments } from "./loomSectionRecipeInvalidation";

export type LoomControlValue = number | boolean | string;
export type LoomControlUnit = LoomParameterMetadata["unit"];

export type LoomControlPathSegment =
  | { kind: "field"; name: string }
  | { kind: "panel"; panelId: string }
  | { kind: "station"; stationId: string }
  | { kind: "index"; index: number };

export interface LoomNodeFieldLocator {
  kind: "node-field";
  nodeId: string;
  path: readonly LoomControlPathSegment[];
}

export interface LoomNumericControlEditor {
  kind: "range";
  hardMin?: number;
  hardMax?: number;
  exclusiveMin: boolean;
  uiMin: number;
  uiMax: number;
  step: number;
  precision: number;
}

export interface LoomBooleanControlEditor { kind: "toggle" }

export interface LoomSelectControlEditor {
  kind: "select";
  options: readonly { value: string; label: string }[];
}

export interface LoomParameterControl {
  id: string;
  locator: LoomNodeFieldLocator;
  label: string;
  group: string;
  unit: LoomControlUnit;
  value: LoomControlValue;
  editor: LoomNumericControlEditor | LoomBooleanControlEditor | LoomSelectControlEditor;
  affects: "shape" | "topology";
  source: "native-schema";
}

export interface LoomControlEdit {
  controlId: string;
  value: LoomControlValue;
}

export interface LoomControlCompileOptions {
  commandId: string;
  expectedRevision: number;
}

type MutableRecord = Record<string, unknown>;
type SelectOption = string | { value: string; label: string };
type AddControl = (
  path: readonly LoomControlPathSegment[],
  value: LoomControlValue,
  label: string,
  group: string,
  metadata: LoomParameterMetadata,
  options?: readonly SelectOption[],
) => void;

const field = (name: string): LoomControlPathSegment => ({ kind: "field", name });
const index = (value: number): LoomControlPathSegment => ({ kind: "index", index: value });
const panel = (panelId: string): LoomControlPathSegment => ({ kind: "panel", panelId });
const station = (stationId: string): LoomControlPathSegment => ({ kind: "station", stationId });

function asMetadata(
  parameters: readonly LoomParameterMetadata[],
  id: string,
  unit: LoomControlUnit,
): LoomParameterMetadata {
  return parameters.find((parameter) => parameter.id === id) ?? { id, unit };
}

function title(value: string): string {
  return value
    .replace(/(M|Deg|Ratio)$/, "")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .trim()
    .replace(/^./, (character) => character.toUpperCase());
}

function niceStep(span: number, integer: boolean): number {
  if (integer) return 1;
  const rough = Math.max(span / 200, Number.EPSILON);
  const magnitude = 10 ** Math.floor(Math.log10(rough));
  const scaled = rough / magnitude;
  const rounded = scaled <= 1 ? 1 : scaled <= 2 ? 2 : scaled <= 5 ? 5 : 10;
  return rounded * magnitude;
}

function defaultSpan(value: number, unit: LoomControlUnit): number {
  const magnitude = Math.abs(value);
  const doubled = magnitude > Number.MAX_VALUE / 2 ? Number.MAX_VALUE / 2 : magnitude * 2;
  if (unit === "ratio") return Math.max(1, doubled);
  if (unit === "deg") return Math.max(20, doubled);
  if (unit === "count") return Math.max(20, doubled);
  if (unit === "coefficient") return Math.max(2, doubled);
  return Math.max(2, doubled);
}

function numericEditor(value: number, metadata: LoomParameterMetadata): LoomNumericControlEditor {
  const hardMin = Number.isFinite(metadata.min) ? metadata.min : undefined;
  const hardMax = Number.isFinite(metadata.max) ? metadata.max : undefined;
  const span = defaultSpan(value, metadata.unit);
  const finiteOr = (candidate: number, fallback: number) => Number.isFinite(candidate) ? candidate : fallback;
  let uiMin = hardMin ?? (hardMax === undefined ? finiteOr(value - span / 2, value) : finiteOr(hardMax - span, value));
  let uiMax = hardMax ?? (hardMin === undefined ? finiteOr(value + span / 2, value) : finiteOr(hardMin + span, value));
  uiMin = Math.min(uiMin, value);
  uiMax = Math.max(uiMax, value);
  if (!(uiMax > uiMin)) uiMax = uiMin + Math.max(1, span);
  const naturalStep = niceStep(uiMax - uiMin, metadata.unit === "count");
  const step = metadata.id === "panels[].sweepDeg" ? 0.01 : naturalStep;
  if (metadata.exclusiveMin && hardMin !== undefined && uiMin <= hardMin) {
    uiMin = Math.min(value, hardMin + step);
  }
  const precision = metadata.unit === "count"
    ? 0
    : Math.max(0, Math.min(8, -Math.floor(Math.log10(step))));
  return {
    kind: "range",
    ...(hardMin === undefined ? {} : { hardMin }),
    ...(hardMax === undefined ? {} : { hardMax }),
    exclusiveMin: metadata.exclusiveMin === true,
    uiMin,
    uiMax,
    step,
    precision,
  };
}

function pathId(nodeId: string, path: readonly LoomControlPathSegment[]): string {
  const encoded = path.map((segment) => {
    if (segment.kind === "field") return `f:${encodeURIComponent(segment.name)}`;
    if (segment.kind === "panel") return `p:${encodeURIComponent(segment.panelId)}`;
    if (segment.kind === "station") return `s:${encodeURIComponent(segment.stationId)}`;
    return `i:${segment.index}`;
  });
  return `node:${encodeURIComponent(nodeId)}/${encoded.join("/")}`;
}

export function formatLoomControlPath(path: readonly LoomControlPathSegment[]): string {
  return path.reduce((result, segment) => {
    if (segment.kind === "field") return result ? `${result}.${segment.name}` : segment.name;
    if (segment.kind === "panel") return result ? `${result}.panels[${segment.panelId}]` : `panels[${segment.panelId}]`;
    if (segment.kind === "station") return result ? `${result}.stations[${segment.stationId}]` : `stations[${segment.stationId}]`;
    return `${result}[${segment.index}]`;
  }, "");
}

function addProfileControls(
  profile: LoomProfile2d,
  path: readonly LoomControlPathSegment[],
  group: string,
  add: AddControl,
): void {
  const parameters = LOOM_PROFILE_CATALOG.find((entry) => entry.id === profile.kind)?.parameters
    ?? [];
  if (profile.kind === "circle") {
    add([...path, field("radiusM")], profile.radiusM, "Radius", group, asMetadata(parameters, "radiusM", "m"));
  } else if (profile.kind === "rounded-box") {
    profile.halfExtentsM.forEach((value, axis) => add(
      [...path, field("halfExtentsM"), index(axis)], value, `Half extent ${"XY"[axis]}`, group,
      asMetadata(parameters, "halfExtentsM[]", "m"),
    ));
    add([...path, field("cornerRadiusM")], profile.cornerRadiusM, "Corner radius", group, asMetadata(parameters, "cornerRadiusM", "m"));
  } else if (profile.kind === "ellipse" || profile.kind === "superellipse") {
    profile.semiAxesM.forEach((value, axis) => add(
      [...path, field("semiAxesM"), index(axis)], value, `Semi-axis ${"XY"[axis]}`, group,
      asMetadata(parameters, "semiAxesM[]", "m"),
    ));
    if (profile.kind === "superellipse") {
      add([...path, field("widthExponent")], profile.widthExponent, "Width exponent", group, asMetadata(parameters, "widthExponent", "ratio"));
      add([...path, field("heightExponent")], profile.heightExponent, "Height exponent", group, asMetadata(parameters, "heightExponent", "ratio"));
    }
  } else {
    profile.verticesM.forEach((vertex, vertexIndex) => vertex.forEach((value, axis) => add(
      [...path, field("verticesM"), index(vertexIndex), index(axis)], value,
      `Vertex ${vertexIndex + 1} ${"XY"[axis]}`, group, asMetadata(parameters, "verticesM[]", "m"),
    )));
  }
}

function addAirfoilControls(
  airfoil: LoomAirfoil,
  path: readonly LoomControlPathSegment[],
  group: string,
  add: AddControl,
): void {
  const parameters = LOOM_AIRFOIL_CATALOG.find((entry) => entry.id === airfoil.family)?.parameters
    ?? [];
  add(
    [...path, field("family")], airfoil.family, "Airfoil family", group,
    asMetadata(parameters, "family", "enum"),
    LOOM_AIRFOIL_CATALOG.map((entry) => ({ value: entry.id, label: entry.label })),
  );
  if (airfoil.family === "naca4") {
    for (const name of ["camberRatio", "camberLocationRatio", "thicknessRatio", "trailingEdgeThicknessRatio"] as const) {
      add([...path, field(name)], airfoil[name], title(name), group, asMetadata(parameters, name, "ratio"));
    }
    add([...path, field("closedTrailingEdge")], airfoil.closedTrailingEdge, "Closed trailing edge", group, asMetadata(parameters, "closedTrailingEdge", "boolean"));
  } else if (airfoil.family === "cst") {
    for (const name of ["upperCoefficients", "lowerCoefficients"] as const) {
      airfoil[name].forEach((value, coefficientIndex) => add(
        [...path, field(name), index(coefficientIndex)], value,
        `${name === "upperCoefficients" ? "Upper" : "Lower"} coefficient ${coefficientIndex + 1}`,
        group, asMetadata(parameters, `${name}[]`, "coefficient"),
      ));
    }
  } else {
    airfoil.vertices.forEach((vertex, vertexIndex) => vertex.forEach((value, axis) => add(
      [...path, field("vertices"), index(vertexIndex), index(axis)], value,
      `Vertex ${vertexIndex + 1} ${"XZ"[axis]}`, group, asMetadata(parameters, "vertices[]", "ratio"),
    )));
    add([...path, field("closedTrailingEdge")], airfoil.closedTrailingEdge, "Closed trailing edge", group, asMetadata(parameters, "closedTrailingEdge", "boolean"));
    add([...path, field("trailingEdgeThicknessRatio")], airfoil.trailingEdgeThicknessRatio, "Trailing edge thickness ratio", group, asMetadata(parameters, "trailingEdgeThicknessRatio", "ratio"));
  }
}

function addPrimitiveControls(node: Extract<LoomSceneNode, { type: "primitive" }>, add: AddControl): void {
  const definition = node.definition;
  const parameters = LOOM_PRIMITIVE_CATALOG.find((entry) => entry.id === definition.primitive)?.parameters
    ?? [];
  const group = node.label;
  if (definition.primitive === "sphere") {
    add([field("radiusM")], definition.radiusM, "Radius", group, asMetadata(parameters, "radiusM", "m"));
  } else if (definition.primitive === "box") {
    definition.dimensionsM.forEach((value, axis) => add(
      [field("dimensionsM"), index(axis)], value, `Dimension ${"XYZ"[axis]}`,
      group, asMetadata(parameters, "dimensionsM[]", "m"),
    ));
  } else if (definition.primitive === "ellipsoid") {
    definition.radiiM.forEach((value, axis) => add(
      [field("radiiM"), index(axis)], value, `Radius ${"XYZ"[axis]}`,
      group, asMetadata(parameters, "radiiM[]", "m"),
    ));
  } else if (definition.primitive === "extrusion") {
    add([field("halfLengthM")], definition.halfLengthM, "Half length", group, asMetadata(parameters, "halfLengthM", "m"));
    addProfileControls(definition.profile, [field("profile")], `${group} · profile`, add);
    add([field("plane")], definition.plane, "Plane", group, asMetadata(parameters, "plane", "enum"), ["xy", "xz", "yz"]);
  } else if (definition.primitive === "revolution") {
    add([field("radialOffsetM")], definition.radialOffsetM, "Radial offset", group, asMetadata(parameters, "radialOffsetM", "m"));
    addProfileControls(definition.profile, [field("profile")], `${group} · profile`, add);
    add([field("axis")], definition.axis, "Axis", group, asMetadata(parameters, "axis", "enum"), ["x", "y", "z"]);
  } else if (definition.primitive === "pod") {
    for (const [name, unit] of [["lengthM", "m"], ["finenessRatio", "ratio"], ["noseXM", "m"]] as const) {
      add([field(name)], definition[name], title(name), group, asMetadata(parameters, name, unit));
    }
    add([field("profile")], definition.profile, "Profile", group, asMetadata(parameters, "profile", "enum"), ["capsule", "ellipse"]);
  } else if (definition.primitive === "wing") {
    definition.panels.forEach((wingPanel) => {
      const panelPath = [panel(wingPanel.panelId)];
      const panelGroup = `${group} · panel ${wingPanel.panelId}`;
      const fields = [
        ["yRootM", "m"], ["yTipM", "m"], ["chordRootM", "m"], ["chordTipM", "m"],
        ["sweepDeg", "deg"], ["sweepLocationRatio", "ratio"], ["dihedralDeg", "deg"],
        ["twistRootDeg", "deg"], ["twistTipDeg", "deg"], ["twistLocationRatio", "ratio"],
        ["xOffsetM", "m"], ["zOffsetM", "m"], ["sectionSamples", "count"],
      ] as const;
      fields.forEach(([name, unit]) => add(
        [...panelPath, field(name)], wingPanel[name], title(name), panelGroup,
        asMetadata(parameters, `panels[].${name}`, unit),
      ));
      addAirfoilControls(wingPanel.airfoilRoot, [...panelPath, field("airfoilRoot")], `${panelGroup} · root airfoil`, add);
      addAirfoilControls(wingPanel.airfoilTip, [...panelPath, field("airfoilTip")], `${panelGroup} · tip airfoil`, add);
    });
    add([field("tipRadiusM")], definition.tipRadiusM, "Tip radius", group, asMetadata(parameters, "tipRadiusM", "m"));
    add([field("tip")], definition.tip, "Tip", group, asMetadata(parameters, "tip", "enum"), ["flat", "round"]);
  } else {
    definition.stations.forEach((fuselageStation) => {
      const stationPath = [station(fuselageStation.stationId)];
      const stationGroup = `${group} · station ${fuselageStation.stationId}`;
      for (const name of ["xM", "zCenterM"] as const) {
        add([...stationPath, field(name)], fuselageStation[name], title(name), stationGroup, asMetadata(parameters, `stations[].${name}`, "m"));
      }
      if (fuselageStation.kind === "superellipse") {
        for (const [name, unit] of [["widthM", "m"], ["heightM", "m"], ["widthExponent", "ratio"], ["heightExponent", "ratio"]] as const) {
          add([...stationPath, field(name)], fuselageStation[name], title(name), stationGroup, asMetadata(parameters, `stations[].${name}`, unit));
        }
      }
    });
  }
}

function addTransformControls(node: Extract<LoomSceneNode, { type: "transform" }>, add: AddControl): void {
  const operation = node.operation;
  const parameters = LOOM_TRANSFORM_CATALOG.find((entry) => entry.id === operation.transform)?.parameters
    ?? [];
  if (operation.transform === "translate") {
    operation.offsetM.forEach((value, axis) => add([field("offsetM"), index(axis)], value, `Offset ${"XYZ"[axis]}`, node.label, asMetadata(parameters, "offsetM[]", "m")));
  } else if (operation.transform === "uniform-scale") {
    add([field("factor")], operation.factor, "Scale factor", node.label, asMetadata(parameters, "factor", "ratio"));
  } else if (operation.transform === "rotate") {
    add([field("angleDeg")], operation.angleDeg, "Angle", node.label, asMetadata(parameters, "angleDeg", "deg"));
    operation.originM.forEach((value, axis) => add([field("originM"), index(axis)], value, `Origin ${"XYZ"[axis]}`, node.label, asMetadata(parameters, "originM[]", "m")));
    add([field("axis")], operation.axis, "Axis", node.label, asMetadata(parameters, "axis", "enum"), ["x", "y", "z"]);
  } else if (operation.transform === "mirror") {
    add([field("axis")], operation.axis, "Reflection plane", node.label, asMetadata(parameters, "axis", "enum"), ["x", "y", "z"]);
    add([field("planeOffsetM")], operation.planeOffsetM, "World plane coordinate", node.label, asMetadata(parameters, "planeOffsetM", "m"));
  }
}

function controlsForNode(node: LoomSceneNode): LoomParameterControl[] {
  const controls: LoomParameterControl[] = [];
  const add: AddControl = (path, value, label, group, metadata, options) => {
    const topology = metadata.unit === "count"
      || metadata.unit === "enum"
      || path.some((segment) => segment.kind === "field" && segment.name === "closedTrailingEdge");
    const editor = typeof value === "boolean"
      ? { kind: "toggle" as const }
      : typeof value === "number"
        ? numericEditor(value, metadata)
        : { kind: "select" as const, options: (options ?? []).map((option) => typeof option === "string" ? { value: option, label: title(option) } : option) };
    if (editor.kind === "select" && !editor.options.some((option) => option.value === value)) {
      throw new Error(`control ${pathId(node.id, path)} has no option for ${value}`);
    }
    controls.push({
      id: pathId(node.id, path),
      locator: { kind: "node-field", nodeId: node.id, path },
      label,
      group,
      unit: metadata.unit,
      value,
      editor,
      affects: topology ? "topology" : "shape",
      source: "native-schema",
    });
  };
  if (node.type === "primitive") addPrimitiveControls(node, add);
  else if (node.type === "transform") addTransformControls(node, add);
  else if (node.type === "boolean" && node.operation === "smooth-union") {
    add([field("blendRadiusM")], node.blendRadiusM ?? 0, "Blend radius", node.label, { id: "blendRadiusM", unit: "m", min: 0 });
  }
  return controls;
}

export function deriveLoomParameterControls(documentValue: unknown, nodeId?: string): LoomParameterControl[] {
  const document = parseLoomAuthoringDocument(documentValue);
  if (nodeId !== undefined && !document.nodes.some((node) => node.id === nodeId)) {
    throw new Error(`node ${nodeId} does not exist`);
  }
  return document.nodes
    .filter((node) => nodeId === undefined || node.id === nodeId)
    .flatMap(controlsForNode);
}

function mutableRecord(value: unknown, label: string): MutableRecord {
  if (typeof value !== "object" || value === null || Array.isArray(value)) throw new Error(`${label} is not an object`);
  return value as MutableRecord;
}

function defaultAirfoil(family: LoomAirfoil["family"], cstOrder = 4): LoomAirfoil {
  if (family === "naca4") return {
    family, camberRatio: 0, camberLocationRatio: 0, thicknessRatio: 0.12,
    closedTrailingEdge: true, trailingEdgeThicknessRatio: 0.01,
  };
  if (family === "cst") { const seed = [0.17, 0.14, 0.08, 0.03]; const upperCoefficients = Array.from({ length: cstOrder }, (_, i) => seed[i] ?? 0); return { family, upperCoefficients, lowerCoefficients: upperCoefficients.map((coefficient) => -coefficient) }; }
  return {
    family,
    vertices: [[0, 0], [0.25, 0.08], [0.6, 0.06], [1, 0.005], [1, -0.005], [0.6, -0.06], [0.25, -0.08]],
    closedTrailingEdge: true,
    trailingEdgeThicknessRatio: 0.01,
  };
}

function matchingCstOrder(root: unknown, path: readonly LoomControlPathSegment[]): number {
  const definition = root as LoomPrimitiveDefinition;
  const panelSegment = path.find((segment) => segment.kind === "panel");
  const airfoilSegment = path.find((segment) => segment.kind === "field" && (segment.name === "airfoilRoot" || segment.name === "airfoilTip"));
  if (definition.primitive !== "wing" || panelSegment?.kind !== "panel" || airfoilSegment?.kind !== "field") return 4;
  const wingPanel = definition.panels.find((candidate) => candidate.panelId === panelSegment.panelId);
  const opposite = airfoilSegment.name === "airfoilRoot" ? wingPanel?.airfoilTip : wingPanel?.airfoilRoot;
  return opposite?.family === "cst" ? opposite.upperCoefficients.length : 4;
}

function setPathValue(root: unknown, path: readonly LoomControlPathSegment[], value: LoomControlValue): void {
  let cursor = root;
  const cstOrder = matchingCstOrder(root, path);
  path.forEach((segment, pathIndex) => {
    const last = pathIndex === path.length - 1;
    if (segment.kind === "field") {
      const record = mutableRecord(cursor, `control path field ${segment.name}`);
      if (!(segment.name in record)) throw new Error(`control path field ${segment.name} does not exist`);
      if (last && segment.name === "family" && typeof record.family === "string") {
        const replacement = defaultAirfoil(value as LoomAirfoil["family"], cstOrder);
        Object.keys(record).forEach((key) => delete record[key]);
        Object.assign(record, replacement);
      } else if (last) record[segment.name] = value;
      else cursor = record[segment.name];
    } else if (segment.kind === "index") {
      if (!Array.isArray(cursor) || segment.index < 0 || segment.index >= cursor.length) throw new Error(`control path index ${segment.index} does not exist`);
      if (last) cursor[segment.index] = value;
      else cursor = cursor[segment.index];
    } else {
      const collectionName = segment.kind === "panel" ? "panels" : "stations";
      const identityName = segment.kind === "panel" ? "panelId" : "stationId";
      const identity = segment.kind === "panel" ? segment.panelId : segment.stationId;
      const collection = mutableRecord(cursor, `control path ${segment.kind}`)[collectionName];
      if (!Array.isArray(collection)) throw new Error(`control path ${collectionName} does not exist`);
      const match = collection.find((item) => mutableRecord(item, collectionName)[identityName] === identity);
      if (!match) throw new Error(`control path ${segment.kind} ${identity} does not exist`);
      if (last) throw new Error(`control path cannot end at ${segment.kind} ${identity}`);
      cursor = match;
    }
  });
}

function validateControlValue(control: LoomParameterControl, value: LoomControlValue): void {
  if (control.editor.kind === "toggle") {
    if (typeof value !== "boolean") throw new Error(`control ${control.id} requires a boolean value`);
    return;
  }
  if (control.editor.kind === "select") {
    if (typeof value !== "string" || !control.editor.options.some((option) => option.value === value)) {
      throw new Error(`control ${control.id} requires one of its declared options`);
    }
    return;
  }
  if (typeof value !== "number" || !Number.isFinite(value)) throw new Error(`control ${control.id} requires a finite numeric value`);
  if (control.unit === "count" && !Number.isInteger(value)) throw new Error(`control ${control.id} requires an integer value`);
  const { hardMin, hardMax, exclusiveMin } = control.editor;
  if (hardMin !== undefined && (exclusiveMin ? value <= hardMin : value < hardMin)) throw new Error(`control ${control.id} is below its hard minimum`);
  if (hardMax !== undefined && value > hardMax) throw new Error(`control ${control.id} is above its hard maximum`);
}

function commandForNode(
  node: LoomSceneNode,
  root: unknown,
  options: LoomControlCompileOptions,
): LoomAuthoringCommand {
  const base = { schema: "loom-authoring-command/v1" as const, commandId: options.commandId, expectedRevision: options.expectedRevision };
  if (node.type === "primitive") return { ...base, kind: "primitive.replace", nodeId: node.id, definition: root as LoomPrimitiveDefinition };
  if (node.type === "transform") return { ...base, kind: "transform.replace", nodeId: node.id, inputNodeId: node.inputNodeId, operation: root as LoomTransform };
  if (node.type === "boolean") return { ...base, kind: "boolean.replace", nodeId: node.id, operation: node.operation, inputNodeIds: node.inputNodeIds, blendRadiusM: mutableRecord(root, "Boolean controls").blendRadiusM as number };
  throw new Error(`node ${node.id} has no editable native controls`);
}

export function compileLoomControlEdits(
  documentValue: unknown,
  edits: readonly LoomControlEdit[],
  options: LoomControlCompileOptions,
): LoomAuthoringCommand {
  const document: LoomAuthoringDocument = parseLoomAuthoringDocument(documentValue);
  if (options.expectedRevision !== document.revision) throw new Error(`revision conflict: expected ${options.expectedRevision}, current ${document.revision}`);
  if (edits.length === 0) throw new Error("at least one Loom control edit is required");
  if (new Set(edits.map((edit) => edit.controlId)).size !== edits.length) throw new Error("Loom control edits contain duplicate control ids");
  const controls = new Map(deriveLoomParameterControls(document).map((control) => [control.id, control]));
  const selected = edits.map((edit) => {
    const control = controls.get(edit.controlId);
    if (!control) throw new Error(`unknown Loom control ${edit.controlId}`);
    validateControlValue(control, edit.value);
    return { edit, control };
  });
  const nodeIds = new Set(selected.map(({ control }) => control.locator.nodeId));
  if (nodeIds.size !== 1) throw new Error("a Loom control batch must target exactly one node");
  const nodeId = selected[0]!.control.locator.nodeId;
  const node = document.nodes.find((candidate) => candidate.id === nodeId)!;
  const root = structuredClone(node.type === "primitive" ? node.definition : node.type === "transform" ? node.operation : node);
  invalidateEditedLoomSectionTreatments(root, selected.filter(({ edit, control }) => edit.value !== control.value).map(({ control }) => control.locator.path));
  selected.forEach(({ edit, control }) => setPathValue(root, control.locator.path, edit.value));
  const command = commandForNode(node, root, options);
  reduceLoomAuthoringCommand(document, command);
  return command;
}

export function compileLoomControlEdit(
  documentValue: unknown,
  edit: LoomControlEdit,
  options: LoomControlCompileOptions,
): LoomAuthoringCommand {
  return compileLoomControlEdits(documentValue, [edit], options);
}
